// First import: Zod reads jitless when the first schema is built.
import '@/lib/configureZod'
import { CONFIG } from '@/config/config'
import { OnboardingLayout } from '@/components/OnboardingLayout'
import { AnalyticsProvider } from '@/components/shared/AnalyticsProvider'
import { ThemeProvider } from '@/components/ThemeProvider'
import { Toaster } from '@/components/ui/sonner'
import { AdminClient, AdminUser } from '@storefronts/common-types'
import type { AppProps } from 'next/app'
import Head from 'next/head'
import { useRouter } from 'next/router'
import Script from 'next/script'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { useEffect, useState } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import '../../styles/globals.css'
import { AuthLayout } from '../components/AuthLayout'
import { BasicLayout, StorefrontPreviewLayout } from '../components/BasicLayout'
import { MaintenanceLayout } from '../components/MaintenanceLayout'
import { SettingsLayout } from '../components/settings/SettingsLayout'
import DatadogInit from '../components/DatadogInit'
import { PendoInit } from '../components/shared/PendoInit'
import { AuthContext } from '../modules/auth/authContext'
import { unprotectedRoutes } from '../modules/auth/unprotectedRoutes'
import { isSettingsPath } from '@/lib/settings/settingsRoutes'
import { isStorefrontPreviewPath } from '@/modules/helpers/storefrontPreview'

// Create a client
const queryClient = new QueryClient()

// Check that PostHog is client-side (used to handle Next.js SSR)
if (typeof window !== 'undefined') {
    const POSTHOG_API_KEY = process.env.NEXT_PUBLIC_API_POSTHOG_PUBLIC_KEY
    if (POSTHOG_API_KEY && window.location.hostname !== 'localhost') {
        posthog.init(POSTHOG_API_KEY, {
            api_host: 'https://app.posthog.com',
            // Enable debug mode in development
            loaded: (posthog) => {
                if (process.env.NODE_ENV === 'development') posthog.debug()
            },
        })
    }
}

function MyApp({ Component, pageProps }: AppProps) {
    const [user, setUser] = useState<null | AdminUser>(null)
    const [client, setClient] = useState<null | AdminClient>(null)
    const [isLoggedIn, setLoggedIn] = useState<null | boolean>(null)
    const [isSuperUser, setIsSuperUser] = useState(false)
    const router = useRouter()

    const state = {
        user,
        client,
        isLoggedIn,
        isSuperUser,
        setUser,
        setClient,
        setLoggedIn,
        setIsSuperUser,
    }

    const isOnboardingRoute = router.pathname.includes('/onboarding')

    // Check if this is a preview route (no sidebar/navigation)
    const isPreviewRoute = isStorefrontPreviewPath(router.pathname)

    useEffect(() => {
        // Track page views
        const handleRouteChange = () => posthog?.capture('$pageview')
        router.events.on('routeChangeComplete', handleRouteChange)

        return () => {
            router.events.off('routeChangeComplete', handleRouteChange)
        }
    }, [router.events])

    useEffect(() => {
        document.documentElement.classList.toggle(
            'storefront-preview',
            isPreviewRoute,
        )
        return () => {
            document.documentElement.classList.remove('storefront-preview')
        }
    }, [isPreviewRoute])

    // Check if maintenance mode is enabled via environment variable
    const isMaintenanceMode =
        process.env.NEXT_PUBLIC_MAINTENANCE_MODE === 'true'

    const isSettingsRoute = isSettingsPath(router.pathname)

    const Layout = isMaintenanceMode
        ? MaintenanceLayout
        : isPreviewRoute
          ? StorefrontPreviewLayout
          : unprotectedRoutes.includes(router.pathname)
            ? BasicLayout
            : isOnboardingRoute
              ? OnboardingLayout
              : AuthLayout

    return (
        <QueryClientProvider client={queryClient}>
            <PostHogProvider client={posthog}>
                <ThemeProvider attribute="class" defaultTheme="light">
                    <Head>
                        <link rel="shortcut icon" href="/favicon.png" />
                        <title>Storefronts</title>
                        <link
                            rel="preconnect"
                            href="https://fonts.googleapis.com"
                        ></link>
                        <link
                            rel="preconnect"
                            href="https://fonts.gstatic.com"
                            crossOrigin="anonymous"
                        ></link>
                        <link
                            href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter&display=swap"
                            rel="stylesheet"
                        ></link>
                        <link
                            href="https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600&display=swap"
                            rel="stylesheet"
                        ></link>
                        <DatadogInit />
                    </Head>
                    <AuthContext.Provider value={state}>
                        <AnalyticsProvider>
                            <Layout>
                                {isSettingsRoute ? (
                                    <SettingsLayout pathname={router.pathname}>
                                        <Component {...pageProps} />
                                    </SettingsLayout>
                                ) : (
                                    <Component {...pageProps} />
                                )}
                                <Toaster position="bottom-right" richColors />
                            </Layout>
                        </AnalyticsProvider>
                        <PendoInit />
                        {/* Production Clarity project; pentest traffic must not land in it. */}
                        {!CONFIG.IS_STAGING && !isPreviewRoute && (
                            <Script
                                id="clarity-script"
                                strategy="afterInteractive"
                            >
                                {`
            (function(c,l,a,r,i,t,y){
                c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
            })(window, document, "clarity", "script", "ocvmmqgf9f");
          `}
                            </Script>
                        )}
                    </AuthContext.Provider>
                </ThemeProvider>
            </PostHogProvider>
        </QueryClientProvider>
    )
}

export default MyApp
